import React, { useEffect, useMemo, useState } from "react";
import { useBrand } from "../context/BrandContext";
import {
  resolveClubImageUrl,
  resolveClubLogoUrl,
} from "../utils/resolveClubImageUrl";
import ClubPlaceholderIcon from "./ClubPlaceholderIcon";
import "./ClubLogoMark.css";

/** First capital letter for a club letter-avatar (skips punctuation / spaces). */
export function clubLetterInitial(name) {
  const raw = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
  if (!raw) return "?";
  const match = raw.match(/[A-Za-z0-9]/);
  return (match?.[0] || raw[0] || "?").toUpperCase();
}

/**
 * Club logo / icon — image when available; otherwise a letter avatar
 * (or TT crest when no club name is known).
 */
export function ClubLogoMark({
  club = null,
  clubImage = null,
  clubName = null,
  useBrandFallback = false,
  size = "sm",
  className = "",
  imgClassName = "",
  onClick = null,
  onLoad = null,
  onError = null,
  title = "",
  /** "auto" | "letter" | "icon" — auto prefers letter when a name exists */
  fallback = "auto",
}) {
  const { brand } = useBrand();
  const [failed, setFailed] = useState(false);

  const rawImage = clubImage ?? club?.club_image ?? null;
  const resolvedName =
    clubName ||
    club?.club_name ||
    club?.club_short_name ||
    club?.name ||
    title ||
    "";

  useEffect(() => {
    setFailed(false);
  }, [rawImage, useBrandFallback, brand?.logo_url, brand?.favicon_url]);

  const logoUrl = useMemo(() => {
    if (failed) return null;
    if (useBrandFallback) {
      return resolveClubLogoUrl(rawImage, brand);
    }
    return resolveClubImageUrl(rawImage);
  }, [rawImage, brand, useBrandFallback, failed]);

  const initial = useMemo(() => clubLetterInitial(resolvedName), [resolvedName]);
  const useLetter =
    fallback === "letter" ||
    (fallback === "auto" && Boolean(resolvedName && initial && initial !== "?"));

  const sizeClass = `club-logo-mark--${size}`;
  const rootClass = ["club-logo-mark", sizeClass, className].filter(Boolean).join(" ");
  const iconPx = size === "lg" ? 72 : size === "md" ? 30 : 24;

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        title={title || resolvedName}
        className={[rootClass, imgClassName].filter(Boolean).join(" ")}
        onClick={onClick}
        onLoad={onLoad}
        onError={(e) => {
          setFailed(true);
          onError?.(e);
        }}
      />
    );
  }

  return (
    <span
      className={`${rootClass} club-logo-mark--fallback${
        useLetter ? " club-logo-mark--letter" : ""
      }`}
      onClick={onClick}
      title={title || resolvedName}
      role={onClick ? "button" : undefined}
      aria-label={resolvedName || title || undefined}
      aria-hidden={!resolvedName && !title}
    >
      {useLetter ? (
        <span className="club-logo-mark__letter" aria-hidden>
          {initial}
        </span>
      ) : (
        <ClubPlaceholderIcon
          className="club-logo-mark__icon"
          sx={{ fontSize: iconPx }}
        />
      )}
    </span>
  );
}

export default ClubLogoMark;
